
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { debugLogger } from '@/utils/debugLogger';
import { toast } from 'sonner';

export const useMissionUserManagement = (missionId: string) => {
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [isRemovingUser, setIsRemovingUser] = useState(false);

  const addUser = async (userId: string) => {
    setIsAddingUser(true);
    try {
      debugLogger.debug('Adding user to mission:', { userId, missionId });

      // Check if user is already in this mission
      const { data: existingMission, error: checkError } = await supabase
        .from('user_missions')
        .select('id')
        .eq('user_id', userId)
        .eq('mission_id', missionId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      if (existingMission) {
        toast.error('Usuário já está participando desta missão');
        return;
      }

      // Add user to mission
      const { data: userMission, error: insertError } = await supabase
        .from('user_missions')
        .insert({
          user_id: userId,
          mission_id: missionId,
          current_day: 1,
          status: 'ongoing'
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Create initial progress for all days
      const progressEntries = [];
      for (let day = 1; day <= 10; day++) {
        progressEntries.push({
          user_mission_id: userMission.id,
          day_number: day,
          status: day === 1 ? 'available' : 'pending'
        });
      }

      const { error: progressError } = await supabase
        .from('user_mission_progress')
        .insert(progressEntries);

      if (progressError) throw progressError;

      toast.success('Usuário adicionado à missão com sucesso!');
      
    } catch (error) {
      debugLogger.error('Error adding user to mission:', error);
      toast.error('Erro ao adicionar usuário à missão');
    } finally {
      setIsAddingUser(false);
    }
  };

  const removeUser = async (userMissionId: string) => {
    setIsRemovingUser(true);
    try {
      debugLogger.debug('Removing user from mission:', userMissionId);

      // Remove user mission progress
      const { error: progressError } = await supabase
        .from('user_mission_progress')
        .delete()
        .eq('user_mission_id', userMissionId);

      if (progressError) throw progressError;

      // Remove user mission
      const { error: missionError } = await supabase
        .from('user_missions')
        .delete()
        .eq('id', userMissionId);

      if (missionError) throw missionError;

      toast.success('Usuário removido da missão com sucesso!');
      
    } catch (error) {
      debugLogger.error('Error removing user from mission:', error);
      toast.error('Erro ao remover usuário da missão');
    } finally {
      setIsRemovingUser(false);
    }
  };

  return {
    addUser,
    removeUser,
    isAddingUser,
    isRemovingUser
  };
};
